import { useState, useRef } from "react";
import S from "../styles/styles";
import { supabase } from "../utils/supabase";

const R2_FUNCTION_URL = process.env.REACT_APP_SUPABASE_FUNCTION_URL
  ? process.env.REACT_APP_SUPABASE_FUNCTION_URL.replace("stripe-payout", "r2-upload")
  : "";

const uploadToR2 = async (file, folder) => {
  // Get presigned URL from Edge Function
  const session = await supabase.auth.getSession();
  const token   = session?.data?.session?.access_token || "";

  const res = await fetch(R2_FUNCTION_URL, {
    method: "POST",
    headers: {
      "Content-Type":  "application/json",
      "Authorization": `Bearer ${token}`,
      "apikey":        process.env.REACT_APP_SUPABASE_ANON_KEY || "",
    },
    body: JSON.stringify({
      fileName: file.name,
      fileType: file.type,
      folder,
    }),
  });

  if (!res.ok) throw new Error("Failed to get upload URL");
  const { presignedUrl, fileUrl, key } = await res.json();

  // Upload directly to R2
  const uploadRes = await fetch(presignedUrl, {
    method: "PUT",
    headers: { "Content-Type": file.type },
    body: file,
  });

  if (!uploadRes.ok) throw new Error("R2 upload failed");
  return { fileUrl, storagePath: key };
};

const DOC_TYPES = [
  "Lease Agreement",
  "Move-In Inspection",
  "Move-Out Inspection",
  "ID / Verification",
  "Insurance Certificate",
  "Building Permit",
  "Strata Document",
  "Contractor Invoice",
  "Other",
];

const DOC_ICONS = {
  "Lease Agreement":      "📄",
  "Move-In Inspection":   "🔍",
  "Move-Out Inspection":  "🔍",
  "ID / Verification":    "🪪",
  "Insurance Certificate":"🛡",
  "Building Permit":      "🏗",
  "Strata Document":      "🏢",
  "Contractor Invoice":   "🧾",
  "Other":                "📎",
};

const formatDate = (iso) => {
  if (!iso) return "-";
  const [y, m, d] = iso.split("T")[0].split("-");
  const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  return `${months[parseInt(m)-1]} ${parseInt(d)}, ${y}`;
};

const formatSize = (bytes) => {
  if (!bytes) return "-";
  if (bytes < 1024) return bytes + " B";
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
  return (bytes / (1024 * 1024)).toFixed(1) + " MB";
};

const EmptyState = ({ icon, title, subtitle }) => (
  <div style={{ padding: "48px 24px", textAlign: "center" }}>
    <div style={{ fontSize: 36, marginBottom: 12 }}>{icon}</div>
    <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 6 }}>{title}</div>
    {subtitle && <div style={{ fontSize: 13, color: "#9993A8" }}>{subtitle}</div>}
  </div>
);

const DocumentsTab = ({ documents = [], properties = [], users = [], onAddDocument, onDeleteDocument }) => {
  const [subTab,       setSubTab]       = useState("renters");
  const [uploading,    setUploading]    = useState(false);
  const [uploadError,  setUploadError]  = useState("");
  const [showForm,     setShowForm]     = useState(false);
  const [filterProp,   setFilterProp]   = useState("");
  const [filterRenter, setFilterRenter] = useState("");
  const [filterType,   setFilterType]   = useState("");

  const fileRef  = useRef(null);
  const EMPTY_FORM = { name: "", type: "Lease Agreement", renterId: "", propertyId: "", notes: "" };
  const [form, setForm] = useState(EMPTY_FORM);
  const [selectedFile, setSelectedFile] = useState(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const renters = users.filter((u) => u.role === "renter");

  // -- Upload handler ---------------------------------------------------------
  const handleUpload = async () => {
    if (!selectedFile) { setUploadError("Please select a file"); return; }
    if (!form.propertyId && !form.renterId) { setUploadError("Select a property or tenant"); return; }

    setUploading(true);
    setUploadError("");

    try {
      const folder   = form.renterId ? `renters/${form.renterId}` : `properties/${form.propertyId}`;
      const renter   = renters.find((u) => String(u.id) === String(form.renterId));
      const property = properties.find((p) => String(p.id) === String(form.propertyId));

      // Upload to Cloudflare R2
      const { fileUrl, storagePath } = await uploadToR2(selectedFile, folder);

      const doc = {
        name:         form.name || selectedFile.name,
        type:         form.type,
        renterId:     form.renterId || null,
        renterName:   renter?.name || null,
        propertyId:   form.propertyId || (renter?.propertyId ? String(renter.propertyId) : null),
        propertyName: property?.address || property?.name || (renter ? properties.find((p) => p.id === renter.propertyId)?.address : null) || "-",
        notes:        form.notes,
        storagePath,
        fileUrl,
        fileName:     selectedFile.name,
        fileSize:     selectedFile.size,
        fileType:     selectedFile.type,
        uploadedAt:   new Date().toISOString(),
      };

      if (onAddDocument) await onAddDocument(doc);

      setForm(EMPTY_FORM);
      setSelectedFile(null);
      setShowForm(false);
      if (fileRef.current) fileRef.current.value = "";
    } catch (err) {
      console.error("Upload failed:", err);
      setUploadError(err.message || "Upload failed - check Cloudflare R2 is configured and Edge Function is deployed");
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (doc) => {
    if (!window.confirm(`Delete "${doc.name}"?`)) return;
    // Note: R2 file deletion requires backend - file URL remains valid but DB record removed
    // For full cleanup, add R2 delete to Edge Function later
    if (onDeleteDocument) await onDeleteDocument(doc.id);
  };

  const handleDownload = (doc) => {
    if (doc.fileUrl) {
      window.open(doc.fileUrl, "_blank");
    }
  };

  // -- Filtered lists ---------------------------------------------------------
  const renterDocs   = documents.filter((d) => d.renterId);
  const propertyDocs = documents.filter((d) => d.propertyId); // shows ALL docs for a property including tenant docs

  const filteredRenter = renterDocs.filter((d) => {
    if (filterRenter && String(d.renterId) !== String(filterRenter)) return false;
    if (filterProp   && String(d.propertyId) !== String(filterProp)) return false;
    if (filterType   && d.type !== filterType) return false;
    return true;
  });

  const filteredProperty = propertyDocs.filter((d) => {
    if (filterProp && String(d.propertyId) !== String(filterProp)) return false;
    if (filterType && d.type !== filterType) return false;
    return true;
  });

  const tabs = [
    { id: "renters",    label: `Tenant Docs (${renterDocs.length})`   },
    { id: "properties", label: `Property Docs (${propertyDocs.length})` },
  ];

  return (
    <div>
      <div style={S.pageTitle}>Documents</div>
      <div style={S.pageSub}>Lease agreements, inspections, permits and more</div>

      {/* Stats */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(130px,1fr))", gap: 14, marginBottom: 28 }}>
        {[
          { label: "Total Documents",   value: documents.length,       color: "#2A7BE8" },
          { label: "Tenant Docs",        value: renterDocs.length,      color: "#E8622A" },
          { label: "Property Docs",      value: propertyDocs.length,    color: "#22AA55" },
          { label: "Lease Agreements",    value: documents.filter((d) => d.type === "Lease Agreement").length,    color: "#635BFF" },
          { label: "Contractor Invoices", value: documents.filter((d) => d.type === "Contractor Invoice").length, color: "#F5C842" },
        ].map((st) => (
          <div key={st.label} style={{ background: "#141418", border: "1px solid #ffffff08", borderRadius: 12, padding: "16px 18px" }}>
            <div style={{ fontSize: 28, fontWeight: 800, color: st.color }}>{st.value}</div>
            <div style={{ fontSize: 11, color: "#9993A8", fontWeight: 600, marginTop: 4 }}>{st.label}</div>
          </div>
        ))}
      </div>

      {/* Sub-tabs + Upload button */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20, flexWrap: "wrap", gap: 10 }}>
        <div style={{ ...S.tabRow, maxWidth: 380, marginBottom: 0 }}>
          {tabs.map(({ id, label }) => (
            <button key={id} style={{ ...S.tabBtn, ...(subTab === id ? S.tabBtnActive : {}) }} onClick={() => setSubTab(id)}>
              {label}
            </button>
          ))}
        </div>
        <button style={{ ...S.btnPrimary, fontSize: 12, padding: "8px 16px" }} onClick={() => setShowForm(true)}>
          + Upload Document
        </button>
      </div>

      {/* Upload Form */}
      {showForm && (
        <div style={{ background: "#141418", border: "1px solid #E8622A44", borderRadius: 14, padding: 24, marginBottom: 24 }}>
          <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 20 }}>Upload Document</div>

          {/* File picker */}
          <div style={{ ...S.field, marginBottom: 16 }}>
            <label style={S.label}>File *</label>
            <input
              ref={fileRef}
              type="file"
              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.heic"
              style={{ ...S.input, cursor: "pointer" }}
              onChange={(e) => {
                const f = e.target.files[0];
                setSelectedFile(f);
                if (f && !form.name) set("name", f.name.replace(/\.[^.]+$/, ""));
              }}
            />
            {selectedFile && (
              <div style={{ fontSize: 11, color: "#22AA55", marginTop: 4 }}>
                {selectedFile.name} - {formatSize(selectedFile.size)}
              </div>
            )}
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 14, marginBottom: 14 }}>
            <div style={S.field}>
              <label style={S.label}>Document Name</label>
              <input style={S.input} placeholder="e.g. John Smith Lease 2025" value={form.name} onChange={(e) => set("name", e.target.value)} />
            </div>
            <div style={S.field}>
              <label style={S.label}>Document Type</label>
              <select style={S.select} value={form.type} onChange={(e) => set("type", e.target.value)}>
                {DOC_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div style={S.field}>
              <label style={S.label}>Property</label>
              <select style={S.select} value={form.propertyId} onChange={(e) => { set("propertyId", e.target.value); set("renterId", ""); }}>
                <option value="">-- Select property --</option>
                {properties.map((p) => <option key={p.id} value={p.id}>{p.address || p.name}</option>)}
              </select>
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 16 }}>
            <div style={S.field}>
              <label style={S.label}>Tenant (optional)</label>
              <select style={S.select} value={form.renterId} onChange={(e) => {
                set("renterId", e.target.value);
                const r = renters.find((u) => String(u.id) === e.target.value);
                if (r && !form.propertyId) set("propertyId", String(r.propertyId || ""));
              }}>
                <option value="">-- Attach to tenant --</option>
                {renters.filter((u) => !form.propertyId || String(u.propertyId) === String(form.propertyId)).map((u) => (
                  <option key={u.id} value={u.id}>{u.name} - Unit {u.unit}</option>
                ))}
              </select>
            </div>
            <div style={S.field}>
              <label style={S.label}>Notes</label>
              <input style={S.input} placeholder="Optional notes" value={form.notes} onChange={(e) => set("notes", e.target.value)} />
            </div>
          </div>

          {uploadError && (
            <div style={{ ...S.errorBox, marginBottom: 14 }}>{uploadError}</div>
          )}

          <div style={{ background: "#2A7BE810", border: "1px solid #2A7BE830", borderRadius: 8, padding: "10px 14px", marginBottom: 16, fontSize: 12, color: "#9993A8" }}>
            Files are stored securely in Supabase Storage. Max 50MB per file. Supported: PDF, DOC, DOCX, JPG, PNG.
          </div>

          <div style={{ display: "flex", gap: 10 }}>
            <button
              style={{ ...S.btnPrimary, padding: "10px 20px", fontSize: 13, opacity: uploading ? 0.6 : 1 }}
              onClick={handleUpload}
              disabled={uploading}
            >
              {uploading ? "Uploading..." : "Upload"}
            </button>
            <button style={{ ...S.btnSecondary, padding: "10px 20px", fontSize: 13 }} onClick={() => { setShowForm(false); setUploadError(""); }}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Filters */}
      <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap" }}>
        <select style={{ ...S.select, width: "auto", fontSize: 12, padding: "6px 10px" }}
          value={filterProp} onChange={(e) => setFilterProp(e.target.value)}>
          <option value="">All Properties</option>
          {properties.map((p) => <option key={p.id} value={p.id}>{p.address || p.name}</option>)}
        </select>
        {subTab === "renters" && (
          <select style={{ ...S.select, width: "auto", fontSize: 12, padding: "6px 10px" }}
            value={filterRenter} onChange={(e) => setFilterRenter(e.target.value)}>
            <option value="">All Tenants</option>
            {renters.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
        )}
        <select style={{ ...S.select, width: "auto", fontSize: 12, padding: "6px 10px" }}
          value={filterType} onChange={(e) => setFilterType(e.target.value)}>
          <option value="">All Types</option>
          {DOC_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
      </div>

      {/* Document list */}
      {(() => {
        const list = subTab === "renters" ? filteredRenter : filteredProperty;
        return (
          <div style={S.tableWrap}>
            <div style={{ display: "grid", gridTemplateColumns: "36px 1fr 140px 160px 100px 140px", gap: 12, padding: "10px 20px", background: "#0F0F14", borderBottom: "1px solid #ffffff08" }}>
              {["", "Document", "Type", subTab === "renters" ? "Tenant" : "Property", "Size", "Actions"].map((h) => (
                <div key={h} style={{ fontSize: 11, fontWeight: 700, color: "#5A5570", textTransform: "uppercase", letterSpacing: "0.06em" }}>{h}</div>
              ))}
            </div>

            {list.map((doc) => (
              <div key={doc.id} style={{ display: "grid", gridTemplateColumns: "36px 1fr 140px 160px 100px 140px", gap: 12, padding: "14px 20px", borderBottom: "1px solid #ffffff06", alignItems: "center" }}>
                <div style={{ fontSize: 20, textAlign: "center" }}>{DOC_ICONS[doc.type] || "📎"}</div>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>{doc.name}</div>
                  <div style={{ fontSize: 11, color: "#9993A8", marginTop: 2 }}>{formatDate(doc.uploadedAt)} {doc.notes ? `. ${doc.notes}` : ""}</div>
                </div>
                <span style={{ fontSize: 11, fontWeight: 600, padding: "3px 8px", borderRadius: 99, background: "#635BFF18", color: "#635BFF" }}>
                  {doc.type}
                </span>
                <div style={{ fontSize: 12, color: "#9993A8" }}>
                  {subTab === "renters" ? (doc.renterName || "-") : (doc.propertyName || "-")}
                </div>
                <div style={{ fontSize: 12, color: "#9993A8" }}>{formatSize(doc.fileSize)}</div>
                <div style={{ display: "flex", gap: 6 }}>
                  {doc.fileUrl && (
                    <button style={{ ...S.btnSmall, background: "#2A7BE818", color: "#2A7BE8", fontSize: 11, padding: "4px 10px" }}
                      onClick={() => handleDownload(doc)}>
                      View
                    </button>
                  )}
                  <button style={{ ...S.btnSmall, background: "#FF2D2D18", color: "#FF2D2D", fontSize: 11, padding: "4px 10px" }}
                    onClick={() => handleDelete(doc)}>
                    Delete
                  </button>
                </div>
              </div>
            ))}

            {list.length === 0 && (
              <EmptyState
                icon="📁"
                title="No documents yet"
                subtitle={documents.length ? "Try changing the filters" : "Upload your first document using the button above"}
              />
            )}
          </div>
        );
      })()}
    </div>
  );
};

export default DocumentsTab;
